import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Loader2, ShieldCheck, Mail, Phone, Pencil, Building2, Bookmark, ChevronRight, CreditCard, Coins } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { useAuth } from "@/lib/auth";
import { sharedApi } from "@/lib/api";
import { TotpSecurityCard, TotpNudge } from "@/components/auth/TotpSecurity";

/**
 * AccountPage (Iter-243) — customer profile with a "Delete my account" row.
 * Follows the founder's UX contract: row → confirmation screen where the
 * user must type DELETE (any other input → 400) → call `DELETE /auth/me`
 * → sign out locally on 200 → land on home.
 */
/** Iter 799 — "Verify your e-mail" banner with Resend. Reads the LIVE
 *  flag from /auth/me (the localStorage user can be stale) and posts
 *  /auth/verify-email/request (204) on tap. */
function VerifyEmailBanner() {
  const meQ = useQuery({
    queryKey: ["auth-me-verify"],
    queryFn: async () => (await sharedApi.get("/auth/me")).data,
    staleTime: 60_000,
    retry: 0,
  });
  const resendMut = useMutation({
    mutationFn: async () => sharedApi.post("/auth/verify-email/request", {}),
    onSuccess: () => toast.success("Verification e-mail sent — check your inbox (and spam)."),
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send the e-mail — try again shortly.");
    },
  });
  if (!meQ.data || meQ.data.email_verified !== false) return null;
  return (
    <div
      data-testid="verify-email-banner"
      className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
    >
      <Mail className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-amber-900">Verify your e-mail</div>
        <p className="mt-0.5 text-xs text-amber-800 leading-relaxed">
          We need to confirm {meQ.data.email} before we can send you receipts, alerts and sign-in codes.
        </p>
      </div>
      <button
        type="button"
        data-testid="verify-email-resend"
        disabled={resendMut.isPending}
        onClick={() => resendMut.mutate()}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-xs font-bold px-4 py-2 transition-colors"
      >
        {resendMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Resend e-mail
      </button>
    </div>
  );
}

export default function AccountPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");

  const savedSearchesQ = useQuery({
    queryKey: ["saved-searches", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/saved-searches");
      return data?.saved_searches || [];
    },
    enabled: !!user,
    staleTime: 60_000,
    retry: 0,
  });
  const savedCount = savedSearchesQ.data?.length || 0;

  const deleteMut = useMutation({
    mutationFn: async () => {
      // Backend requires exact string "DELETE" for `confirm` — anything
      // else → 400. Reason is optional.
      const { data } = await sharedApi.delete("/auth/me", {
        data: { confirm: "DELETE", reason: reason || undefined },
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Your account has been deleted.");
      // Iter-274f fix (iter-98 review): React batches nav() + logout() in
      // the same success callback, so RequireAuth wins the race and
      // redirects the still-mounted /account route to /login. Do a HARD
      // browser navigation instead — it leaves this document entirely
      // before any auth state is inspected.
      try { logout(); } catch { /* noop */ }
      window.location.assign("/");
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't delete your account. Please try again."
      );
    },
  });

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="account-page">
      <Seo title="Account · Allsale Classified" />
      <Breadcrumbs items={[{ label: "Account" }]} />

      <h1 className="mt-4 font-display text-3xl md:text-4xl font-bold text-slate-900">
        My account
      </h1>

      {/* Iter-363 — authenticator adoption nudge (once per user) */}
      <TotpNudge />

      {/* Iter 799 — recovery path for members whose verify e-mail was lost
          in the 17–28 Oct outage: live email_verified from /auth/me, with
          a working Resend button. */}
      <VerifyEmailBanner />

      <section className="mt-6 rounded-3xl bg-white shadow-card p-6" data-testid="account-profile-card">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
            Profile
          </div>
          <button
            type="button"
            onClick={() => nav("/account/edit")}
            data-testid="account-edit-open"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue)] hover:text-white font-semibold px-4 py-1.5 text-xs transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit profile
          </button>
        </div>
        <div className="space-y-2 text-sm">
          {user?.picture && (
            <div className="flex items-center gap-2">
              <span className="w-24 text-slate-500">Photo</span>
              <img
                src={user.picture}
                alt=""
                data-testid="account-photo"
                className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-200"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="w-24 text-slate-500">Name</span>
            <span data-testid="account-name" className="font-semibold text-slate-800">
              {user?.full_name || user?.name || "—"}
            </span>
          </div>
          {user?.account_type === "business" && (
            <div className="flex items-center gap-2">
              <span className="w-24 text-slate-500">Business</span>
              <span data-testid="account-business-name" className="font-semibold text-indigo-700 inline-flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                {user?.business?.name || user?.business_name || "—"}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-slate-400" />
            <span data-testid="account-email" className="text-slate-700">
              {user?.email || "—"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-3.5 h-3.5 text-slate-400" />
            <span data-testid="account-phone" className="text-slate-700">
              {user?.phone || "Not set"}
            </span>
            {user?.phone_verified && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-600">
                <ShieldCheck className="w-3 h-3" /> Verified
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Iter-363 — authenticator-app 2FA settings */}
      <TotpSecurityCard />

      {/* Iter 720 — password reset entry for signed-in users who don't
          remember their current password. */}
      <section className="mt-6 rounded-3xl bg-white shadow-card p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Password</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Forgot it? We'll email you a reset link.
            </p>
          </div>
          <Link
            to="/forgot-password"
            data-testid="account-forgot-password-link"
            className="shrink-0 rounded-full border border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] text-xs font-bold px-4 py-2 hover:bg-[color:var(--brand-blue-wash)] transition-colors"
          >
            Reset password
          </Link>
        </div>
      </section>

      <Link
        to="/account/saved-searches"
        data-testid="account-saved-searches-tile"
        className="mt-6 block rounded-3xl bg-white shadow-card p-5 hover:shadow-lg transition-shadow"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center shrink-0">
            <Bookmark className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-semibold text-slate-900">Saved searches</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {savedCount === 0
                ? "No alerts yet — save a search to get emailed on new matches"
                : `${savedCount} ${savedCount === 1 ? "alert" : "alerts"} — we'll email you on new matches`}
            </div>
          </div>
          {savedCount > 0 && (
            <span
              data-testid="account-saved-searches-count"
              className="inline-flex items-center justify-center rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] text-xs font-bold px-2.5 py-1 min-w-[28px]"
            >
              {savedCount}
            </span>
          )}
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </div>
      </Link>

      {/* Iter-597 — saved cards manager */}
      <Link
        to="/account/payment-methods"
        data-testid="account-payment-methods-tile"
        className="mt-6 block rounded-3xl bg-white shadow-card p-5 hover:shadow-lg transition-shadow"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center shrink-0">
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-semibold text-slate-900">💳 Payment methods</div>
            <div className="text-xs text-slate-500 mt-0.5">Saved cards for faster checkout</div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </div>
      </Link>

      {/* Iter-600 §3 — gold wallet shortcut (NZ reward coins) */}
      <Link
        to="/rewards"
        data-testid="account-wallet-tile"
        className="mt-6 block rounded-3xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 shadow-card p-5 hover:shadow-lg transition-shadow"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 text-white grid place-items-center shrink-0">
            <Coins className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-semibold text-slate-900">My Wallet — Reward Coins</div>
            <div className="text-xs text-amber-700 mt-0.5">Check your balance, redeem for dollars off at partner stores</div>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-400" />
        </div>
      </Link>

      <section
        className="mt-6 rounded-3xl bg-white border border-red-200 p-6"
        data-testid="account-danger-zone"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-2xl bg-red-100 text-red-600 grid place-items-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">
              Danger zone
            </div>
            <div className="font-display text-lg font-semibold text-slate-900">
              Delete my account
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Your ads and listings will be anonymised. Orders are retained for
              accounting only. This can&apos;t be undone.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setConfirmOpen(true);
              setConfirmText("");
              setReason("");
            }}
            data-testid="account-delete-open"
            className="inline-flex items-center gap-1.5 rounded-full border border-red-500 text-red-600 hover:bg-red-500 hover:text-white font-semibold px-4 py-2 text-xs transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete account
          </button>
        </div>
      </section>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[65] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !deleteMut.isPending && setConfirmOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            className="w-full max-w-md rounded-3xl bg-white shadow-xl overflow-hidden"
            data-testid="account-delete-modal"
          >
            <div className="px-5 py-4 border-b border-subtle">
              <div id="delete-account-title" className="font-display font-bold text-slate-900">
                Delete my account
              </div>
            </div>
            <form
              className="p-5 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (confirmText !== "DELETE" || deleteMut.isPending) return;
                deleteMut.mutate();
              }}
            >
              <p className="text-sm text-slate-600">
                This is permanent. Your profile will be anonymised and you
                won&apos;t be able to sign back in with the same credentials.
              </p>
              <label className="block">
                <span className="text-xs font-semibold text-slate-600">
                  Type <span className="font-mono text-red-600">DELETE</span> to confirm
                </span>
                <input
                  data-testid="account-delete-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-soft bg-white py-2.5 px-3 outline-none focus:border-red-500 font-mono uppercase tracking-widest"
                  placeholder="DELETE"
                  autoComplete="off"
                  required
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-600">
                  Reason (optional)
                </span>
                <textarea
                  data-testid="account-delete-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-soft bg-white py-2.5 px-3 outline-none focus:border-red-500 min-h-[72px]"
                  placeholder="Help us improve — what made you leave?"
                />
              </label>
              <div className="pt-1 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  disabled={deleteMut.isPending}
                  data-testid="account-delete-cancel"
                  className="rounded-full border border-soft bg-white hover:bg-slate-50 text-slate-700 font-semibold px-5 py-2 text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={confirmText !== "DELETE" || deleteMut.isPending}
                  data-testid="account-delete-submit"
                  className="rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-2 text-sm inline-flex items-center gap-2"
                >
                  {deleteMut.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Deleting…
                    </>
                  ) : (
                    "Delete account"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
